/**
 * Utilities for handling dates with proper timezone handling
 */

/**
 * Converts a date to UK timezone (GMT/BST) for consistency
 * @param date The date to convert
 * @returns Date string in ISO format with time set to midnight in UK timezone
 */
export const convertToUTC2 = (date: Date | string): string => {
  if (!date) return '';

  // Create a new date object from the input
  const inputDate = typeof date === 'string' ? new Date(date) : date;

  // Create a new date for the same day at noon to avoid timezone issues
  const ukDate = new Date(
    inputDate.getFullYear(),
    inputDate.getMonth(),
    inputDate.getDate(),
    12, 0, 0
  );

  // Format to ISO string, which will handle timezone conversions
  return ukDate.toISOString();
};

/**
 * Formats a date string for display in DD/MM/YYYY format (UK format)
 * @param dateString ISO date string
 * @returns Formatted date string in DD/MM/YYYY format
 */
export const formatDateDisplay = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  // Format to DD/MM/YYYY (UK format)
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Parses a display date in DD/MM/YYYY format to a Date object
 * @param displayDate Date string in DD/MM/YYYY format
 * @returns Date object
 */
export const parseDisplayDate = (displayDate: string): Date | null => {
  if (!displayDate) return null;

  // Expected format: DD/MM/YYYY (UK format)
  const parts = displayDate.split('/');
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JS Date
  const year = parseInt(parts[2], 10);

  // Set to noon to avoid timezone issues
  const date = new Date(year, month, day, 12, 0, 0);

  // Validate date is valid
  if (isNaN(date.getTime())) return null;

  return date;
};

/**
 * Converts a Date object to a UK formatted date string (DD/MM/YYYY)
 * @param date Date object to format
 * @returns Formatted date string
 */
export const formatToUKDate = (date: Date | null): string => {
  if (!date) return '';
  return formatDateDisplay(date.toISOString());
};